import { useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditPlayerModal({ player, onClose, onSaved }) {
  const { t } = useLang();
  const [data, setData] = useState({
    ...player,
    emergency_contact: { ...(player.emergency_contact || { name: "", phone: "" }) },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const password = localStorage.getItem("st_admin_pw") || "";

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setNested = (parent, k, v) =>
    setData((d) => ({ ...d, [parent]: { ...d[parent], [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        player_number: data.player_number,
        first_name: data.first_name,
        last_name: data.last_name,
        date_of_birth: data.date_of_birth,
        age: Number(data.age) || 0,
        gender: data.gender,
        position: data.position,
        dominant_foot: data.dominant_foot,
        current_club: data.current_club,
        experience: data.experience,
        parent_full_name: data.parent_full_name,
        relationship: data.relationship,
        email: data.email,
        phone: data.phone,
        emergency_contact: data.emergency_contact,
        has_medical_condition: !!data.has_medical_condition,
        medical_condition_details: data.medical_condition_details,
        allergies: data.allergies,
        medications: data.medications,
        guardian_printed_name: data.guardian_printed_name,
      };
      const r = await axios.put(`${API}/admin/players/${player.id}`, payload, {
        headers: { "X-Admin-Password": password },
      });
      onSaved(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none";

  const Field = ({ label, children }) => (
    <div>
      <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="edit-modal"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#161616] border border-[#252525] rounded-2xl p-6 sm:p-8 my-8"
      >
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-[#252525]">
          <div>
            <h2 className="font-bebas text-3xl tracking-widest text-white">
              {t("edit_player")}
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              # <span className="text-[#e8559a] font-bebas tracking-wider">{player.player_number}</span>
              {" · "}{player.first_name} {player.last_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-close-btn"
            className="text-neutral-400 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("player_number_label")}>
            <input
              data-testid="edit-player-number"
              className={inputCls}
              value={data.player_number || ""}
              onChange={(e) =>
                set("player_number", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
              }
            />
          </Field>
          <Field label={t("date_of_birth")}>
            <input
              type="date"
              className={inputCls}
              value={data.date_of_birth || ""}
              onChange={(e) => set("date_of_birth", e.target.value)}
            />
          </Field>
          <Field label={t("first_name")}>
            <input
              data-testid="edit-first-name"
              className={inputCls}
              value={data.first_name || ""}
              onChange={(e) => set("first_name", e.target.value)}
            />
          </Field>
          <Field label={t("last_name")}>
            <input
              data-testid="edit-last-name"
              className={inputCls}
              value={data.last_name || ""}
              onChange={(e) => set("last_name", e.target.value)}
            />
          </Field>
          <Field label={t("age")}>
            <input
              type="number"
              className={inputCls}
              value={data.age || ""}
              onChange={(e) => set("age", e.target.value)}
            />
          </Field>
          <Field label={t("gender")}>
            <select
              className={inputCls}
              value={data.gender || ""}
              onChange={(e) => set("gender", e.target.value)}
            >
              <option value="">{t("select")}</option>
              <option value="male">{t("gender_male")}</option>
              <option value="female">{t("gender_female")}</option>
              <option value="other">{t("gender_other")}</option>
            </select>
          </Field>
          <Field label={t("primary_position")}>
            <select
              className={inputCls}
              value={data.position || ""}
              onChange={(e) => set("position", e.target.value)}
            >
              <option value="">{t("select")}</option>
              <option value="forward">{t("pos_forward")}</option>
              <option value="midfielder">{t("pos_midfielder")}</option>
              <option value="defender">{t("pos_defender")}</option>
              <option value="goalkeeper">{t("pos_goalkeeper")}</option>
            </select>
          </Field>
          <Field label={t("dominant_foot")}>
            <select
              className={inputCls}
              value={data.dominant_foot || ""}
              onChange={(e) => set("dominant_foot", e.target.value)}
            >
              <option value="">{t("select")}</option>
              <option value="right">{t("foot_right")}</option>
              <option value="left">{t("foot_left")}</option>
              <option value="both">{t("foot_both")}</option>
            </select>
          </Field>
          <Field label={t("experience")}>
            <select
              className={inputCls}
              value={data.experience || ""}
              onChange={(e) => set("experience", e.target.value)}
            >
              <option value="">{t("select")}</option>
              <option value="beginner">{t("exp_beginner")}</option>
              <option value="intermediate">{t("exp_intermediate")}</option>
              <option value="advanced">{t("exp_advanced")}</option>
              <option value="competitive">{t("exp_competitive")}</option>
            </select>
          </Field>
          <Field label={t("current_club")}>
            <input
              className={inputCls}
              value={data.current_club || ""}
              onChange={(e) => set("current_club", e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-5 pt-5 border-t border-[#252525]">
          <h3 className="font-bebas text-xl tracking-wider text-[#e8559a] mb-3">
            {t("section_parent")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("parent_full_name")}>
              <input
                data-testid="edit-parent-name"
                className={inputCls}
                value={data.parent_full_name || ""}
                onChange={(e) => set("parent_full_name", e.target.value)}
              />
            </Field>
            <Field label={t("relationship")}>
              <select
                className={inputCls}
                value={data.relationship || ""}
                onChange={(e) => set("relationship", e.target.value)}
              >
                <option value="">{t("select")}</option>
                <option value="father">{t("rel_father")}</option>
                <option value="mother">{t("rel_mother")}</option>
                <option value="guardian">{t("rel_guardian")}</option>
                <option value="other">{t("rel_other")}</option>
              </select>
            </Field>
            <Field label={t("email")}>
              <input
                data-testid="edit-email"
                type="email"
                className={inputCls}
                value={data.email || ""}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label={t("phone")}>
              <input
                data-testid="edit-phone"
                className={inputCls}
                value={data.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
              />
            </Field>
            <Field label={t("emergency_name")}>
              <input
                className={inputCls}
                value={data.emergency_contact?.name || ""}
                onChange={(e) => setNested("emergency_contact", "name", e.target.value)}
              />
            </Field>
            <Field label={t("emergency_phone")}>
              <input
                className={inputCls}
                value={data.emergency_contact?.phone || ""}
                onChange={(e) => setNested("emergency_contact", "phone", e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[#252525]">
          <h3 className="font-bebas text-xl tracking-wider text-[#e8559a] mb-3">
            {t("section_medical")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("has_medical")}>
              <select
                className={inputCls}
                value={data.has_medical_condition ? "yes" : "no"}
                onChange={(e) => set("has_medical_condition", e.target.value === "yes")}
              >
                <option value="no">{t("no")}</option>
                <option value="yes">{t("yes")}</option>
              </select>
            </Field>
            <Field label={t("medical_details")}>
              <input
                className={inputCls}
                value={data.medical_condition_details || ""}
                onChange={(e) => set("medical_condition_details", e.target.value)}
              />
            </Field>
            <Field label={t("allergies")}>
              <input
                className={inputCls}
                value={data.allergies || ""}
                onChange={(e) => set("allergies", e.target.value)}
              />
            </Field>
            <Field label={t("medications")}>
              <input
                className={inputCls}
                value={data.medications || ""}
                onChange={(e) => set("medications", e.target.value)}
              />
            </Field>
            <Field label={t("guardian_printed")}>
              <input
                className={inputCls}
                value={data.guardian_printed_name || ""}
                onChange={(e) => set("guardian_printed_name", e.target.value)}
              />
            </Field>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm" data-testid="edit-error">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-save-btn"
            className="px-6 py-2.5 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas text-lg tracking-widest transition-colors"
          >
            {saving ? t("saving") : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}
